import errorOverlayMiddleware from 'react-dev-utils/errorOverlayMiddleware';
// @ts-ignore
import evalSourceMapMiddleware from 'react-dev-utils/evalSourceMapMiddleware';
import noopServiceWorkerMiddleware from 'react-dev-utils/noopServiceWorkerMiddleware';
import {
  ProxyConfigArray,
  ProxyConfigMap,
  Configuration as WebpackDevServerConfiguration,
} from 'webpack-dev-server';

import { AnyConfiguration, DevConfiguration, Environment } from '../types';
import { getPaths } from '../env';

// @ts-ignore
const host = process.env.HOST || '0.0.0.0';

export function isDevConfig(input: AnyConfiguration): input is DevConfiguration {
  return input && input.mode === 'development';
}

type SelectiveEnv = Pick<Environment, 'locations' | 'projectRoot' | 'https'>;

type DevServerOptions = {
  allowedHost?: string;
  proxy?: ProxyConfigMap | ProxyConfigArray;
};

export default function withDevServer(
  config: AnyConfiguration,
  env: SelectiveEnv,
  options: DevServerOptions = {}
): AnyConfiguration {
  if (isDevConfig(config)) {
    config.devServer = createDevServer(env, options);
  }
  return config;
}

export function createDevServer(
  env: SelectiveEnv,
  { allowedHost, proxy }: DevServerOptions = {}
): WebpackDevServerConfiguration {
  const { https = false } = env;
  const locations = env.locations || getPaths(env.projectRoot);
  // https://github.com/facebook/create-react-app/blob/master/packages/react-scripts/config/webpackDevServer.config.js
  return {
    // Enable gzip compression of generated files.
    compress: true,
    // Silence WebpackDevServer's own logs since they're generally not useful.
    // It will still show compile warnings and errors with this setting.
    clientLogLevel: 'none',
    // https://github.com/facebook/create-react-app/blob/e59e0920f3bef0c2ac47bbf6b4ff3092c8ff08fb/packages/react-scripts/config/webpackDevServer.config.js#L46
    // By default WebpackDevServer serves physical files from current directory
    // in addition to all the virtual build products that it serves from memory.
    // This is confusing because those files won’t automatically be available in
    // production build folder unless we copy them. However, copying the whole
    // project directory is dangerous because we may expose sensitive files.
    // Instead, we establish a convention that only files in `public` directory
    // get served. Our build script will copy `public` into the `build` folder.
    // In `index.html`, you can get URL of `public` folder with %WEB_PUBLIC_URL%:
    // <link rel="shortcut icon" href="%WEB_PUBLIC_URL%/favicon.ico">
    // In JavaScript code, you can access it with `process.env.WEB_PUBLIC_URL`.
    // Note that we only recommend to use `public` folder as an escape hatch
    // for files like `favicon.ico`, `manifest.json`, and libraries that are
    // for some reason broken when imported through Webpack. If you just want to
    // use an image, put it in `src` and `import` it from JavaScript instead.
    contentBase: locations.template.folder,
    // By default files from `contentBase` will not trigger a page reload.
    watchContentBase: true,
    // Enable hot reloading server. It will provide /sockjs-node/ endpoint
    // for the WebpackDevServer client so it can learn when the files were
    // updated. The WebpackDevServer client is included as an entry point
    // in the Webpack development configuration. Note that only changes
    // to CSS are currently hot reloaded. JS changes will refresh the browser.
    hot: true,
    // It is important to tell WebpackDevServer to use the same "root" path
    // as we specified in the config. In development, we always serve from /.
    publicPath: '/',
    // WebpackDevServer is noisy by default so we emit custom message instead
    // by listening to the compiler events with `compiler.hooks[...].tap` calls above.
    quiet: true,

    host,
    overlay: false,
    historyApiFallback: {
      // Paths with dots should still use the history fallback.
      // See https://github.com/facebook/create-react-app/issues/387.
      disableDotRule: true,
    },
    public: allowedHost,
    proxy,

    before(app, server) {
      // if (fs.existsSync(paths.proxySetup)) {
      //   // This registers user provided middleware for proxy reasons
      //   require(paths.proxySetup)(app);
      // }

      // This lets us fetch source contents from webpack for the error overlay
      app.use(evalSourceMapMiddleware(server));
      // This lets us open files from the runtime error overlay.
      app.use(errorOverlayMiddleware());

      // This service worker file is effectively a 'no-op' that will reset any
      // previous service worker registered for the same host:port combination.
      // We do this in development to avoid hitting the production cache if
      // it used the same host and port.
      // https://github.com/facebookincubator/create-react-app/issues/2272#issuecomment-302832432
      app.use(noopServiceWorkerMiddleware());
    },

    // We don't use watchOptions: https://github.com/facebook/create-react-app/blob/e59e0920f3bef0c2ac47bbf6b4ff3092c8ff08fb/packages/react-scripts/config/webpackDevServer.config.js#L79

    // Enable HTTPS if the HTTPS environment variable is set to 'true'
    // https: protocol === 'https',

    https,
    disableHostCheck: !proxy,
    // allowedHosts: [host, 'localhost'],
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, PATCH, OPTIONS',
      'Access-Control-Allow-Headers': 'X-Requested-With, content-type, Authorization',
    },
  };
}
